import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { utils, withTheme } from "@rjsf/core";
import { Theme } from "@rjsf/bootstrap-4";
import useFetch from "../../components/useFetch";
import getDataFromApi from "../../components/getDataFromApi";
import Loader from "../../components/Loader";
import { CustomWidget } from "../../components/widgets/CustomWidgets";
import { CloseIcon, ExclamationIcon } from "../Common/SvgIcons/WorkflowIcons";
import "../admin/tabs/PlansBilling/CustomFeatureModal.scss";
import "../project/data/AddDataset.scss";
import "./Devices.scss";

export const DEVICE_CREDENTIALS_FORM_PATH =
  "forms/devices/update_device_credentials";

const registry = utils.getDefaultRegistry();
Theme.widgets["FileWidget"] = registry.widgets["FileWidget"];
Theme.widgets["RangeWidget"] = registry.widgets["RangeWidget"];
const Form = withTheme(Theme);

const getResponseMessage = (response) => {
  if (!response) return "";
  if (typeof response === "string") return response;
  if (typeof response?.detail === "string") return response.detail;
  return response?.detail?.message || response?.message || "";
};

const normalizeKey = (value = "") =>
  String(value || "")
    .toLowerCase()
    .replace(/[\s-]+/g, "_");

/**
 * Linux SSH user created on Connect. Platform user_id is a hex ObjectId
 * (starts with a digit) so it is prefixed to satisfy Linux username rules.
 */
export function toSessionLinuxUser(userId) {
  const cleaned = String(userId || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_-]/g, "");
  if (!cleaned) return "";
  const prefixed = /^[a-z_]/.test(cleaned) ? cleaned : `u${cleaned}`;
  return prefixed.slice(0, 32);
}

/**
 * Pull SSH/terminal fields from form submit + API response + device detail.
 * Key order is priority — never use device display "name" as SSH username.
 */
export function extractTerminalCredentials(response, formData = {}, device = {}) {
  const bags = [
    response?.credentials,
    response?.data,
    response?.device,
    response?.form_data,
    response,
    formData,
    device,
  ].filter((item) => item && typeof item === "object" && !Array.isArray(item));

  const pick = (...keys) => {
    for (const key of keys) {
      const want = normalizeKey(key);
      for (const bag of bags) {
        for (const [rawKey, rawValue] of Object.entries(bag)) {
          if (rawValue !== null && typeof rawValue === "object") continue;
          if (rawValue === undefined || rawValue === null) continue;
          if (normalizeKey(rawKey) !== want) continue;
          const value = String(rawValue).trim();
          if (!value || value === "--") continue;
          return value;
        }
      }
    }
    return "";
  };

  const bootstrapUser = pick(
    "bootstrap_username",
    "bootstrapUser",
    "bootstrap_user"
  );
  const platformUserId = pick("user_id", "userid", "platform_user_id");
  const sessionUser =
    pick("session_user", "ephemeral_user", "device_ssh_user") ||
    toSessionLinuxUser(platformUserId);

  return {
    deviceId:
      pick(
        "client_id",
        "device_code",
        "stream_id",
        "deviceId"
      ) ||
      device?.client_id ||
      device?.device_code ||
      "",
    deviceLanIp:
      pick(
        "device_ip",
        "deviceLanIp",
        "device_lan_ip",
        "lan_ip",
        "ip_address",
        "ip"
      ) ||
      (device?.device_ip && device.device_ip !== "--" ? device.device_ip : ""),
    bootstrapUser,
    bootstrapPassword: pick(
      "bootstrap_password",
      "bootstrapPassword",
      "bootstrap_passwd"
    ),
    platformUserId,
    // Bootstrap admin (lychee) is only used to create this session user.
    // Never reuse device_username from the device record — that is often lychee.
    deviceUser:
      sessionUser ||
      (bootstrapUser
        ? ""
        : pick(
            "device_username",
            "ssh_user",
            "ssh_username",
            "device_user",
            "deviceUser"
          )),
    sshPassword: pick(
      "device_password",
      "ssh_password",
      "sshPassword",
      "password",
      "passwd"
    ),
    sshKeyPath: pick(
      "ssh_key_path",
      "sshKeyPath",
      "ssh_key",
      "private_key_path"
    ),
  };
}

/**
 * Bootstrap can create the ephemeral session user when:
 * - password is present (lychee / password-only admin), or
 * - user is root with no password (key auth — Ambarella / passwordless flow).
 */
export function canUseBootstrapCredentials(credentials = {}) {
  const user = String(credentials.bootstrapUser || "").trim();
  if (!user) return false;
  if (credentials.bootstrapPassword) return true;
  return user === "root";
}

/**
 * Device id plus either bootstrap admin creds or a session username.
 * LAN IP is optional (reverse / static-reverse modes do not need it).
 */
export function hasUsableTerminalCredentials(credentials = {}) {
  const hasIdentity = Boolean(credentials.deviceId);
  return Boolean(
    hasIdentity &&
      (canUseBootstrapCredentials(credentials) || credentials.deviceUser)
  );
}

/** True when the form GET or device payload already has SSH credentials. */
export function hasSavedCredentials(response, device = {}) {
  const credentials = extractTerminalCredentials(response, {}, device);
  return hasUsableTerminalCredentials(credentials);
}

const OPTIONAL_FIELD_KEYS = new Set([
  "device_password",
  "ssh_password",
  "password",
  "passwd",
  "ssh_key_path",
  "sshkeypath",
  "ssh_key",
  "private_key_path",
  "path",
]);

/** Force password + SSH key path optional even if backend schema marks them required. */
function makePasswordAndPathOptional(schema) {
  if (!schema || typeof schema !== "object") return schema;

  const next = {
    ...schema,
    properties: { ...(schema.properties || {}) },
  };

  Object.keys(next.properties).forEach((key) => {
    if (!OPTIONAL_FIELD_KEYS.has(normalizeKey(key))) return;
    const field = next.properties[key] || {};
    next.properties[key] = {
      ...field,
      // Keep empty string allowed for optional text fields.
      minLength: undefined,
    };
  });

  if (Array.isArray(schema.required)) {
    next.required = schema.required.filter(
      (key) => !OPTIONAL_FIELD_KEYS.has(normalizeKey(key))
    );
  }

  return next;
}

function makeOptionalUiSchema(schema, uiSchema = {}) {
  const next = { ...(uiSchema || {}) };
  Object.keys(schema?.properties || {}).forEach((key) => {
    if (!OPTIONAL_FIELD_KEYS.has(normalizeKey(key))) return;
    next[key] = {
      ...(next[key] || {}),
      "ui:options": {
        ...((next[key] && next[key]["ui:options"]) || {}),
      },
    };
  });
  return next;
}

function buildPrefill(schema, device = {}, savedValues = {}) {
  if (!schema?.properties) return {};

  const next = {};
  Object.keys(schema.properties).forEach((key) => {
    const normalized = normalizeKey(key);
    const field = schema.properties[key];
    if (field?.default !== undefined) {
      next[key] = field.default;
      return;
    }

    // Prefer any values already returned with the schema payload.
    const fromSaved = extractTerminalCredentials(savedValues, {}, device);
    if (
      ["device_username", "device_user", "ssh_user", "username", "user", "user_name", "login"].includes(
        normalized
      ) &&
      fromSaved.deviceUser
    ) {
      next[key] = fromSaved.deviceUser;
      return;
    }
    if (
      ["device_password", "ssh_password", "password", "passwd"].includes(
        normalized
      ) &&
      fromSaved.sshPassword
    ) {
      next[key] = fromSaved.sshPassword;
      return;
    }

    if (
      ["device_id", "deviceid", "id"].includes(normalized) &&
      device?._id
    ) {
      next[key] = device._id;
      return;
    }
    if (
      ["client_id", "device_code", "deviceid", "device_id"].includes(normalized) &&
      (device?.client_id || device?.device_code)
    ) {
      next[key] = device.client_id || device.device_code;
      return;
    }
    if (
      ["device_ip", "lan_ip", "ip", "ip_address", "device_lan_ip"].includes(
        normalized
      ) &&
      device?.device_ip &&
      device.device_ip !== "--"
    ) {
      next[key] = device.device_ip;
      return;
    }
    if (
      ["device_user", "ssh_user", "username", "user"].includes(normalized) &&
      (device?.ssh_user || device?.device_user)
    ) {
      next[key] = device.ssh_user || device.device_user;
    }
  });
  return next;
}

/**
 * Develop App credentials gate.
 * GET forms/devices/update_device_credentials:
 *   - if saved username/password → open terminal directly
 *   - if json_schema → show form once, then POST + open terminal
 */
export default function DeviceCredentialsForm({
  device,
  onClose,
  onSuccess,
  onError,
  initialError = "",
}) {
  const [schema, setSchema] = useState();
  const [uiSchema, setUiSchema] = useState();
  const [formData, setFormData] = useState({});
  const [formInstanceKey, setFormInstanceKey] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(
    () => String(initialError || "")
  );
  const [autoConnecting, setAutoConnecting] = useState(false);

  // Freeze device fields at open time so SSE telemetry updates don't wipe typing.
  const deviceSnapshotRef = useRef(device);
  const schemaReadyRef = useRef(false);
  const autoConnectStartedRef = useRef(false);
  const onSuccessRef = useRef(onSuccess);
  const onErrorRef = useRef(onError);

  useEffect(() => {
    onSuccessRef.current = onSuccess;
    onErrorRef.current = onError;
  }, [onSuccess, onError]);

  useEffect(() => {
    if (!initialError) return;
    setSubmitError(String(initialError));
  }, [initialError]);

  useEffect(() => {
    deviceSnapshotRef.current = device;
    schemaReadyRef.current = false;
    autoConnectStartedRef.current = false;
    // intentionally only on mount / device identity change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [device?._id]);

  const formPath = useMemo(() => {
    const id = deviceSnapshotRef.current?._id || device?._id;
    return id
      ? `${DEVICE_CREDENTIALS_FORM_PATH}?device_id=${encodeURIComponent(id)}`
      : DEVICE_CREDENTIALS_FORM_PATH;
  }, [device?._id]);

  const { data, loading, error } = useFetch(formPath, "get");

  useEffect(() => {
    if (!data) return;
    const snapshot = deviceSnapshotRef.current || device || {};

    // Keep schema ready so we can fall back to the form if auto-connect fails.
    if (!schemaReadyRef.current && data?.json_schema) {
      const optionalSchema = makePasswordAndPathOptional(data.json_schema);
      setSchema(optionalSchema);
      setUiSchema(makeOptionalUiSchema(optionalSchema, data.ui_schema || {}));
      setFormData(buildPrefill(optionalSchema, snapshot, data));
      setFormInstanceKey((key) => key + 1);
      schemaReadyRef.current = true;
    }

    // Parent already failed to connect — keep the form visible with that error.
    if (String(initialError || "").trim()) {
      autoConnectStartedRef.current = true;
      return;
    }

    // Already saved → skip the form and open the terminal.
    if (!hasSavedCredentials(data, snapshot) || autoConnectStartedRef.current) {
      return;
    }
    autoConnectStartedRef.current = true;
    const credentials = extractTerminalCredentials(data, {}, snapshot);
    setAutoConnecting(true);
    Promise.resolve(
      onSuccessRef.current?.(data, credentials, { reused: true })
    )
      .catch((err) => {
        console.error(err);
        autoConnectStartedRef.current = false;
        const message =
          err?.message || "Failed to open terminal with saved credentials.";
        setSubmitError(String(message));
        onErrorRef.current?.(String(message));
      })
      .finally(() => {
        setAutoConnecting(false);
      });
  }, [data, device, initialError]);

  const formSchema = useMemo(
    () => (schema ? { ...schema, title: "" } : schema),
    [schema]
  );

  const closeForm = useCallback(() => {
    if (submitting || autoConnecting) return;
    onClose?.();
  }, [onClose, submitting, autoConnecting]);

  const onSubmit = async ({ formData: submittedFormData }, event) => {
    event?.preventDefault?.();
    setSubmitting(true);
    setSubmitError("");

    const snapshot = deviceSnapshotRef.current || device || {};
    const payload = {
      ...formData,
      ...submittedFormData,
    };
    if (snapshot?._id && payload.device_id == null && payload.deviceId == null) {
      payload.device_id = snapshot._id;
    }

    try {
      const result = await getDataFromApi(
        `${window.ENV.API_URL}/${DEVICE_CREDENTIALS_FORM_PATH}`,
        "post",
        payload
      );

      const credentials = extractTerminalCredentials(result, payload, snapshot);
      await onSuccess?.(result, credentials, { reused: false });
    } catch (err) {
      console.error(err);
      const detail = err?.body?.detail;
      const message =
        (typeof detail === "string" && detail) ||
        detail?.message ||
        err?.body?.message ||
        err?.message ||
        getResponseMessage(err?.body) ||
        "Failed to save device credentials.";
      setSubmitError(String(message));
      onError?.(String(message));
    } finally {
      setSubmitting(false);
    }
  };

  const errorMessage =
    (typeof error?.message === "string" && error.message) ||
    "Failed to load device credentials.";

  const showLoader = (loading && !schema) || autoConnecting;
  const noFormYet = !schema && !showLoader && !error && !submitError;

  return (
    <div className="center-form">
      <div
        className="device-credentials-modal"
        style={{ maxWidth: 560, width: "min(560px, 94vw)" }}
      >
        <div className="cf-head">
          <div>
            <h2>
              Device <strong className="text-primary">Credentials</strong>
            </h2>
            <p className="mb-0 text-secondary" style={{ fontSize: "0.9rem" }}>
              {autoConnecting || (loading && !schema)
                ? "Checking device credentials and opening the terminal…"
                : device?.name
                ? `SSH details for ${device.name} opening the terminal.`
                : "Enter SSH details before opening the terminal."}
            </p>
          </div>
          <button
            type="button"
            className="cf-close text-secondary"
            disabled={submitting || autoConnecting}
            onClick={closeForm}
            aria-label="Close"
          >
            <CloseIcon className="wf-icon text-secondary" />
          </button>
        </div>

        <div className="cf-body">
          {showLoader ? (
            <div className="device-credentials-loading">
              <Loader />
              <p className="device-credentials-loading__text mb-0">
                {autoConnecting
                  ? "Opening Develop App…"
                  : "Loading credentials…"}
              </p>
            </div>
          ) : error ? (
            <div className="device-credentials-alert" role="alert">
              <span className="device-credentials-alert__icon" aria-hidden="true">
                <ExclamationIcon className="wf-icon" />
              </span>
              <div className="device-credentials-alert__copy">
                <strong>Couldn’t load credentials</strong>
                <p className="mb-0">{errorMessage}</p>
              </div>
            </div>
          ) : schema ? (
            <Form
              key={formInstanceKey}
              id="device-credentials-form"
              className="task-collection-form"
              schema={formSchema}
              uiSchema={uiSchema}
              widgets={CustomWidget}
              formData={formData}
              onChange={({ formData: next }) => setFormData(next)}
              onSubmit={onSubmit}
              showErrorList={false}
              noHtml5Validate
              liveValidate={false}
            >
              <button type="submit" className="d-none" aria-hidden tabIndex={-1} />
            </Form>
          ) : noFormYet ? (
            <div className="device-credentials-alert" role="alert">
              <span className="device-credentials-alert__icon" aria-hidden="true">
                <ExclamationIcon className="wf-icon" />
              </span>
              <div className="device-credentials-alert__copy">
                <strong>Credentials unavailable</strong>
                <p className="mb-0">
                  No credentials or form returned from the server.
                </p>
              </div>
            </div>
          ) : null}

          {submitError ? (
            <div className="device-credentials-alert" role="alert">
              <span className="device-credentials-alert__icon" aria-hidden="true">
                <ExclamationIcon className="wf-icon" />
              </span>
              <div className="device-credentials-alert__copy">
                <strong>Couldn’t open Develop App</strong>
                <p className="mb-0">{submitError}</p>
              </div>
            </div>
          ) : null}
        </div>

        {schema && !autoConnecting && !showLoader ? (
          <div className="cf-footer" style={{ justifyContent: "flex-end" }}>
            <button
              type="submit"
              form="device-credentials-form"
              className="cf-submit"
              disabled={submitting}
            >
              {submitting ? "Opening…" : "Save & Open Terminal"}
            </button>
          </div>
        ) : submitError && !schema && !showLoader ? (
          <div className="cf-footer" style={{ justifyContent: "flex-end" }}>
            <button
              type="button"
              className="cf-submit"
              onClick={closeForm}
            >
              Close
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}
